import { ChangeDetectionStrategy, Component, inject, OnDestroy, output, signal } from '@angular/core';
import { CreateRecruitGroupDto, RecruitGroupResponseDto, RecruitGroupTypesEnum } from '@/app/common/general-types/recruit-groups.dto';
import { FormField, form, minLength, required } from '@angular/forms/signals';
import { CurrentUserService } from '@/app/common/current-user/current-user.service';
import { FirebaseService } from '@/app/common/firebase/firebase.service';
import { RecruitGroupService } from '@/app/services/recruit-group/recruit-group.service';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';
import { Subscription } from 'rxjs';
import { ViewportOverlayDirective } from '@/app/common/directives/viewport-overlay/viewport-overlay.directive';

type RecruitGroupPrivacy = 'user' | 'brand' | 'global';

@Component({
  selector: 'cmg-create-group',
  imports: [FormField],
  hostDirectives: [ViewportOverlayDirective],
  templateUrl: './create-group.html',
  styleUrl: './create-group.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown.escape)': 'closeCreateGroup()'
  }
})
export class CreateGroup implements OnDestroy {

  close = output<RecruitGroupResponseDto | undefined>();

  groupCreated = signal<boolean>(false);

  /** New Group Vars */
  newRecruitGroup = signal<CreateRecruitGroupDto | undefined>(undefined);
  submitInProgress = signal<boolean>(false);
  private lastCreatedGroup = signal<RecruitGroupResponseDto | undefined>(undefined);
  private readonly subscriptionList = new Subscription();

  recruitGroupModel = signal<{
    groupName: string;
    groupDescription: string;
    privacy: RecruitGroupPrivacy;
  }>({
    groupName: '',
    groupDescription: '',
    privacy: 'user'
  });
  recruitGroupForm = form(this.recruitGroupModel, (schemaPath) => {
    required(schemaPath.groupName, { message: 'Group name is required' });
    minLength(schemaPath.groupName, 2, { message: 'Group name must be at least 2 characters long' });
    required(schemaPath.privacy, { message: 'Privacy is required' });
  });
  /** New Group Vars */

  /** Services */
  private firebaseService = inject(FirebaseService);
  private currentUserService = inject(CurrentUserService);
  private recruitGroupService = inject(RecruitGroupService);
  private toastNotificationService = inject(ToastNotificationService);
  /** Services - END */

  createGroup() {
    this.submitInProgress.set(true);

    const privacy = this.recruitGroupModel().privacy;
    const newGroup: CreateRecruitGroupDto = {
      groupName: this.recruitGroupModel().groupName,
      groupDescription: this.recruitGroupModel().groupDescription.trim(),
      createdBy: this.firebaseService.userId ?? '',
      isGlobal: privacy === 'global',
      group_type: RecruitGroupTypesEnum.DEFAULT,
    };
    if (privacy === 'brand') {
      newGroup.businessAccountId = this.currentUserService.focussedBrand.id;
    }
    this.newRecruitGroup.set(newGroup);

    this.subscriptionList.add(
      this.recruitGroupService.createRecruitGroup(newGroup).subscribe({
        next: (response) => {
          this.submitInProgress.set(false);
          this.recruitGroupModel.set({
            groupName: '',
            groupDescription: '',
            privacy,
          });
          this.lastCreatedGroup.set(response);
          this.groupCreated.set(true);
        },
        error: (error: unknown) => {
          console.error('submitForm() -> error', error);
          this.submitInProgress.set(false);
          this.groupCreated.set(false);
          this.toastNotificationService.alert({ title: 'Failed to create group', message: 'Failed to create group', type: 'error', icon: 'error' });
        },
      }),
    );
  }

  closeCreateGroup() {
    const created = this.lastCreatedGroup();
    this.lastCreatedGroup.set(undefined);
    this.groupCreated.set(false);
    this.close.emit(created);
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }
}
